import type { TPointData } from '../../../service/entities';

export type Trajectory2dChartTypeId =
  | 'closureMd'
  | 'planNsWe'
  | 'vsecMd'
  | 'tvdMd'
  | 'tvdVsec'
  | 'tvdClosure'
  | 'tvdAhd'
  | 'incAziMd'
  | 'dlsMd'
  | 'ctcMd';

export type Trajectory2dAxisField = {
  field: keyof TPointData;
  labelKey: string;
  /** Used for dual-x charts (e.g. INC/AZI). */
  axisIndex?: number;
};

export type Trajectory2dChartConfig = {
  id: Trajectory2dChartTypeId;
  /** i18n key for settings-list row title. */
  labelKey: string;
  /** One entry for single-x charts; two for dual-x (incAziMd). */
  xFields: Trajectory2dAxisField[];
  yField: Trajectory2dAxisField & { invert?: boolean };
};

export const TRAJECTORY_2D_CHART_CONFIGS: Trajectory2dChartConfig[] = [
  {
    id: 'closureMd',
    labelKey: 'Graph.chartTypes.closureMd',
    xFields: [{ field: 'closure', labelKey: 'TableHeadersShort.closure' }],
    yField: { field: 'md', labelKey: 'TableHeadersShort.md', invert: true },
  },
  {
    id: 'planNsWe',
    labelKey: 'Graph.chartTypes.planNsWe',
    xFields: [{ field: 'we', labelKey: 'TableHeadersShort.we' }],
    yField: { field: 'ns', labelKey: 'TableHeadersShort.ns' },
  },
  {
    id: 'vsecMd',
    labelKey: 'Graph.chartTypes.vsecMd',
    xFields: [{ field: 'vsec', labelKey: 'TableHeadersShort.vsec' }],
    yField: { field: 'md', labelKey: 'TableHeadersShort.md', invert: true },
  },
  {
    id: 'tvdMd',
    labelKey: 'Graph.chartTypes.tvdMd',
    xFields: [{ field: 'md', labelKey: 'TableHeadersShort.md' }],
    yField: { field: 'tvd', labelKey: 'TableHeadersShort.tvd', invert: true },
  },
  {
    id: 'tvdVsec',
    labelKey: 'Graph.chartTypes.tvdVsec',
    xFields: [{ field: 'vsec', labelKey: 'TableHeadersShort.vsec' }],
    yField: { field: 'tvd', labelKey: 'TableHeadersShort.tvd', invert: true },
  },
  {
    id: 'tvdClosure',
    labelKey: 'Graph.chartTypes.tvdClosure',
    xFields: [{ field: 'closure', labelKey: 'TableHeadersShort.closure' }],
    yField: { field: 'tvd', labelKey: 'TableHeadersShort.tvd', invert: true },
  },
  {
    id: 'tvdAhd',
    labelKey: 'Graph.chartTypes.tvdAhd',
    xFields: [{ field: 'ahd', labelKey: 'TableHeadersShort.ahd' }],
    yField: { field: 'tvd', labelKey: 'TableHeadersShort.tvd', invert: true },
  },
  {
    id: 'incAziMd',
    labelKey: 'Graph.chartTypes.incAziMd',
    xFields: [
      { field: 'inc', labelKey: 'TableHeadersShort.inc', axisIndex: 0 },
      { field: 'azi', labelKey: 'TableHeadersShort.azi', axisIndex: 1 },
    ],
    yField: { field: 'md', labelKey: 'TableHeadersShort.md', invert: true },
  },
  {
    id: 'dlsMd',
    labelKey: 'Graph.chartTypes.dlsMd',
    xFields: [{ field: 'dls', labelKey: 'TableHeadersShort.dls' }],
    yField: { field: 'md', labelKey: 'TableHeadersShort.md', invert: true },
  },
  {
    id: 'ctcMd',
    labelKey: 'Graph.chartTypes.ctcMd',
    xFields: [{ field: 'md', labelKey: 'TableHeadersShort.md' }],
    yField: { field: 'centrToCentr', labelKey: 'TableHeadersShort.centrToCentr', invert: false },
  },
];

export const DEFAULT_VISIBLE_2D_CHART_IDS: Trajectory2dChartTypeId[] = ['closureMd'];

export const TRAJECTORY_2D_CHART_IDS = TRAJECTORY_2D_CHART_CONFIGS.map((config) => config.id);

export function getTrajectory2dChartConfig(id: Trajectory2dChartTypeId): Trajectory2dChartConfig | undefined {
  return TRAJECTORY_2D_CHART_CONFIGS.find((config) => config.id === id);
}
